import type { FileRecord } from "@cohesive/api-client";
import { PanelLeftOpen } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  createBlockConfigMessage,
  parseBlockMessage,
  type BlockEntryPoint,
  type BlockToParentMessage,
} from "../../../lib/htmlBlockRuntime";
import {
  blockPrepareOptionsSalt,
  buildBlockPrepareOptions,
  prepareSrcDocHtml,
} from "../../../lib/htmlIframeSrcDoc";
import { cn } from "../../../lib/utils";
import { getFileNameFromPath } from "../FileRecordCard/FileRecordCard";
import { HtmlBlockShield } from "./HtmlBlockShield";
import { HtmlCardPlaceholder } from "./HtmlCardPlaceholder";
import type { CanvasBlockPresentation } from "./htmlCardTypes";
import { useHtmlCardSrcDoc } from "./useHtmlCardSrcDoc";
import {
  useIframeMountAdmission,
  type IframeMountPolicy,
} from "./useIframeMountAdmission";

export type HtmlBlockCardCanvasProps = Pick<
  FileRecord,
  "url" | "path" | "updated_at"
> & {
  className?: string;
  style?: React.CSSProperties;
  selected?: boolean;
  /** The user is INSIDE the block (interact or edit mode) — the chrome reads
   *  blue to distinguish focus-for-interaction from a mere selection. */
  focused?: boolean;
  /** True while the parent NodeResizer is active — the shield remounts so
   *  the resize cursor isn't captured by the iframe. */
  resizing?: boolean;
  /** True when the iframe should receive pointer events (block entered by
   *  double-click, minus gesture exceptions — EntityNode owns that
   *  expression). */
  isInteractive?: boolean;
  /** Block display options from the node's data.presentation. */
  presentation: CanvasBlockPresentation;
  /** `"progressive"` paces the iframe mount through the shared admission
   *  scheduler (public share view); default `"eager"` mounts as soon as the
   *  document is ready — the editor's unchanged behavior. */
  mountPolicy?: IframeMountPolicy;
  /** Server-rendered card preview shown until the live iframe is up —
   *  the share view's placeholder while a progressive mount waits its turn.
   *  Falls back to the generic file glyph when absent. */
  previewImageUrl?: string | null;
  /** Active app theme; drives the injected color-scheme so transparent blocks
   *  composite correctly (mismatched schemes paint an opaque backstop).
   *  Defaults to light. */
  theme?: "light" | "dark";
  /** Optional async rewrite applied to the fetched HTML before rendering —
   *  used to swap embedded public file URLs for presigned ones. Memoize it;
   *  identity changes do not retrigger a fetch. */
  transformHtml?: (html: string) => Promise<string> | string;
  /** Edit session active: the block runtime enables contenteditable islands
   *  and the iframe receives pointer events. */
  islandsEditable?: boolean;
  /** While true, refetched content is stashed instead of committed so the
   *  iframe never remounts mid edit-session. */
  contentFrozen?: boolean;
  /** Capability-aware hover pill text announcing what double-click does on
   *  this block; null/undefined hides the pill (block is live, or loading). */
  hintLabel?: string | null;
  /** Hovering the card should faintly outline editable islands (host-driven
   *  through the runtime — the shield keeps :hover out of the iframe). */
  islandHint?: boolean;
  /** Caret target for an edit session entered by double-click, in iframe
   *  content coordinates. Forwarded with the runtime config. */
  editFocusPoint?: BlockEntryPoint | null;
  /** The runtime announced itself for the current document; `islands` is the
   *  live count of editable islands inside the opaque frame. */
  onRuntimeReady?: (info: { islands: number }) => void;
  /** Renders the hover expand affordance (top-right) that opens the record's
   *  detail pane — the visible route to the pane now that double-click
   *  enters the block instead. */
  onOpenDetail?: () => void;
  /** Runtime messages (island edits, flush/exit requests) — link messages are
   *  handled internally and not forwarded. */
  onBlockMessage?: (msg: BlockToParentMessage) => void;
  onDoubleClick?: (point?: BlockEntryPoint) => void;
};

/**
 * Canvas HTML block: the document rendered 1:1 at exactly the node's size
 * with the app color scheme, Cohesive tokens, fonts, and the cohesive-block
 * runtime injected. One srcDoc serves display, interactive, and edit modes —
 * the parent flips modes via config messages, never remounts, so in-block JS
 * state survives them (see useHtmlCardSrcDoc for the remount discipline).
 *
 * Pointer ownership: unless the block is entered (double-click) or in an
 * edit session, an HtmlBlockShield sits above the iframe so every canvas
 * gesture — wheel pan, drag pan, node drag, marquee — works exactly as over
 * any other node.
 */
export function HtmlBlockCardCanvas({
  url,
  path,
  updated_at,
  className,
  style,
  selected,
  focused = false,
  resizing = false,
  isInteractive = false,
  presentation,
  mountPolicy = "eager",
  previewImageUrl = null,
  theme = "light",
  transformHtml,
  islandsEditable = false,
  contentFrozen = false,
  hintLabel = null,
  islandHint = false,
  editFocusPoint = null,
  onRuntimeReady,
  onOpenDetail,
  onBlockMessage,
  onDoubleClick,
}: HtmlBlockCardCanvasProps) {
  const iframeActive = (isInteractive || islandsEditable) && !resizing;

  const frameless = presentation.frameless === true;
  const transparent = presentation.transparent === true;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hovered, setHovered] = useState(false);

  const prepare = useCallback(
    (transformed: string) =>
      prepareSrcDocHtml(
        transformed,
        buildBlockPrepareOptions({ theme, transparent, runtime: true }),
      ),
    [theme, transparent],
  );

  const { displayDoc } = useHtmlCardSrcDoc({
    url,
    updated_at,
    transformHtml,
    optionsSalt: blockPrepareOptionsSalt(theme, transparent),
    prepare,
    contentFrozen,
  });

  // `ready` keeps this card out of the scheduler until its document can
  // actually mount, so an admission slot always paces a real iframe.
  const { admitted, onIframeLoad } = useIframeMountAdmission({
    policy: mountPolicy,
    hostRef,
    ready: displayDoc !== null,
  });

  const handleIframeLoad = useCallback(() => {
    setIsVisible(true);
    onIframeLoad();
  }, [onIframeLoad]);

  // --- cohesive-block bridge -----------------------------------------------

  const islandsEditableRef = useRef(islandsEditable);
  islandsEditableRef.current = islandsEditable;
  const isInteractiveRef = useRef(isInteractive);
  isInteractiveRef.current = isInteractive;
  const onBlockMessageRef = useRef(onBlockMessage);
  onBlockMessageRef.current = onBlockMessage;
  const onRuntimeReadyRef = useRef(onRuntimeReady);
  onRuntimeReadyRef.current = onRuntimeReady;
  const hintRef = useRef(false);
  hintRef.current = hovered && islandHint;
  const editFocusPointRef = useRef(editFocusPoint);
  editFocusPointRef.current = editFocusPoint;

  const postRuntimeConfig = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      createBlockConfigMessage({
        editable: islandsEditableRef.current,
        interactive: isInteractiveRef.current,
        hint: hintRef.current,
        focusPoint: editFocusPointRef.current,
      }),
      "*",
    );
  }, []);

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      const frame = iframeRef.current;
      // Authenticate by source, NOT origin: every opaque-origin frame on the
      // page reports its origin as the string "null", so an origin check
      // would accept messages from any other block.
      if (!frame || event.source !== frame.contentWindow) return;
      const msg = parseBlockMessage(event.data);
      if (!msg) return;
      if (msg.type === "ready") {
        postRuntimeConfig();
        onRuntimeReadyRef.current?.({ islands: msg.islands ?? 0 });
        return;
      }
      if (msg.type === "link") {
        // Must open synchronously: the click's user activation propagates to
        // this frame but expires almost immediately.
        let parsed: URL;
        try {
          parsed = new URL(msg.href);
        } catch {
          return;
        }
        if (parsed.protocol !== "https:") return;
        window.open(parsed.href, "_blank", "noopener,noreferrer");
        return;
      }
      onBlockMessageRef.current?.(msg);
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [postRuntimeConfig]);

  // Mode flips (and hover-hint / caret-target changes) re-send config to the
  // already-mounted runtime (fresh mounts are covered by the ready handshake).
  useEffect(() => {
    postRuntimeConfig();
  }, [
    islandsEditable,
    isInteractive,
    hovered,
    islandHint,
    editFocusPoint,
    postRuntimeConfig,
  ]);

  // New document → fade back in on its load event.
  const mountKey = displayDoc?.mountKey ?? null;
  useEffect(() => {
    if (mountKey) setIsVisible(false);
  }, [mountKey]);

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onDoubleClick) return;
      // Convert to iframe content coordinates: the card may be scaled by the
      // React Flow viewport, so divide the on-screen offset back down by the
      // rendered/layout width ratio.
      const el = event.currentTarget;
      const rect = el.getBoundingClientRect();
      const scale = el.offsetWidth > 0 ? rect.width / el.offsetWidth : 1;
      onDoubleClick({
        x: (event.clientX - rect.left) / scale,
        y: (event.clientY - rect.top) / scale,
      });
    },
    [onDoubleClick],
  );

  const displayTitle = (path && getFileNameFromPath(path)) || "HTML";

  return (
    <div
      ref={hostRef}
      className={cn(
        "group relative h-full w-full overflow-hidden",
        !frameless && "rounded-xl",
        !frameless && !resizing && "transition-shadow duration-200",
        !frameless && "hover:shadow-2xl",
        selected && !focused && "ring-2 ring-canvas-selection/60",
        focused && "ring-2 ring-spectrum-blue",
        className,
      )}
      style={{ colorScheme: theme, ...style }}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Placeholder until the live document paints: the server-rendered
          card preview when we have one (share view), else the file glyph.
          Frameless blocks skip the glyph box, and transparent blocks skip the
          preview too — the JPEG is opaque, and an opaque plate under a
          see-through block breaks the canvas showing through (worse than
          briefly showing nothing). */}
      {!isVisible && (
        <HtmlCardPlaceholder
          previewImageUrl={transparent ? null : previewImageUrl}
          showGlyph={!frameless}
        />
      )}
      <div
        className={cn(
          "absolute inset-0 transition-opacity duration-200 ease-out",
          isVisible ? "opacity-100" : "opacity-0",
          !transparent && !frameless && "bg-card",
        )}
        style={{ colorScheme: theme }}
      >
        {displayDoc && admitted && (
          <iframe
            key={displayDoc.mountKey}
            ref={iframeRef}
            srcDoc={displayDoc.srcDoc}
            className="h-full w-full border-0"
            style={{
              pointerEvents: iframeActive ? "auto" : "none",
              colorScheme: theme,
              background: "transparent",
            }}
            sandbox="allow-scripts"
            title={displayTitle}
            onLoad={handleIframeLoad}
          />
        )}
      </div>
      {/* Host-DOM hit plate: guarantees canvas gestures (wheel pan, drag pan,
          marquee) work over the block whenever the content doesn't own the
          pointer. Double-clicks bubble to the card root. */}
      {!iframeActive && <HtmlBlockShield hintLabel={hintLabel} />}
      {/* Hover expand affordance → detail pane. Hidden while the block is
          live (the grip strip is the pane route then) and while resizing. */}
      {onOpenDetail && !iframeActive && !resizing && (
        <button
          type="button"
          aria-label="Open"
          title="Open"
          className={cn(
            "nodrag absolute right-1.5 top-1.5 z-[2] flex h-7 w-7 items-center justify-center",
            // Blur only for fine pointers: on touch devices these plates are
            // ~2 per block, and dozens of always-in-DOM backdrop-filter layers
            // measurably burden WebKit's compositor on big canvases. The /90
            // background alone stays legible.
            "rounded-md border border-n-10 bg-n-00/90 text-n-60 shadow-elev-5 pointer-fine:backdrop-blur-sm",
            "opacity-0 transition-opacity duration-150 group-hover:opacity-100",
            "hover:bg-n-05 hover:text-n-80 focus-visible:opacity-100",
          )}
          onClick={(event) => {
            event.stopPropagation();
            onOpenDetail();
          }}
          onDoubleClick={(event) => event.stopPropagation()}
        >
          {/* Same glyph and icon size as the node toolbar's "Open" action. */}
          <PanelLeftOpen className="size-4" />
        </button>
      )}
    </div>
  );
}
